import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppSelector } from '@store/hooks';
import { apiBaseUrl } from '@/config/environment';
import type { EmailSettings } from '@/types';

type SavedEmailSettings = {
  from?: {
    email?: string;
    name?: string;
  };
  smtp?: {
    host?: string;
    auth?: {
      user?: string;
    };
  };
  host?: string;
  username?: string;
  ingestion?: unknown;
};

const hasConfiguredEmailSettings = (
  profileEmailSettings?: EmailSettings | null,
  savedSettings?: SavedEmailSettings | null,
  isGoogleUser = false
) => {
  if (!isGoogleUser) {
    return Boolean(
      profileEmailSettings?.host ||
      profileEmailSettings?.username ||
      savedSettings?.host ||
      savedSettings?.username ||
      savedSettings?.smtp?.host ||
      savedSettings?.smtp?.auth?.user
    );
  }

  if (
    profileEmailSettings?.enabled ||
    profileEmailSettings?.provider ||
    profileEmailSettings?.host ||
    profileEmailSettings?.username ||
    profileEmailSettings?.fromEmail ||
    profileEmailSettings?.fromName ||
    profileEmailSettings?.ingestionSettings
  ) {
    return true;
  }

  return Boolean(
    savedSettings?.ingestion ||
    savedSettings?.from?.email ||
    savedSettings?.from?.name ||
    savedSettings?.host ||
    savedSettings?.username ||
    savedSettings?.smtp?.host ||
    savedSettings?.smtp?.auth?.user
  );
};

export interface OnboardingStatus {
  calendarConnected: boolean;
  emailConfigured: boolean;
  isComplete: boolean;
  isLoading: boolean;
  recheck: () => void;
}

/**
 * Checks whether the two required onboarding integrations are set up:
 *  1. Google Calendar — via /integrations/google/status
 *  2. Email (Gmail / SMTP) — via the saved user profile settings with
 *     localStorage compatibility for older flows
 */
export function useOnboardingStatus(): OnboardingStatus {
  const authUser = useAppSelector(state => state.auth.user);
  const authUserProfile = useAppSelector(state => state.auth.userProfile);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const isMountedRef = useRef(true);
  const calendarRequestIdRef = useRef(0);

  const recheck = useCallback(() => setTick(t => t + 1), []);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const handleWindowFocus = () => {
      recheck();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        recheck();
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [recheck]);

  // Email: read from Redux-backed profile first, then localStorage fallbacks.
  useEffect(() => {
    let savedSettings: SavedEmailSettings | null = null;
    const isGoogleUser =
      authUser?.providerData?.some(provider => provider.providerId === 'google.com') || false;

    const saved = localStorage.getItem('drahft_email_settings');
    if (saved) {
      try {
        savedSettings = JSON.parse(saved) as SavedEmailSettings;
      } catch {
        savedSettings = null;
      }
    }

    setEmailConfigured(
      hasConfiguredEmailSettings(
        authUserProfile?.emailSettings ?? null,
        savedSettings,
        isGoogleUser
      )
    );

    // Also watch for cross-tab updates
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'drahft_email_settings') recheck();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [authUser, authUserProfile, tick, recheck]);

  // Google Calendar: fetch status from backend using the Redux auth user.
  useEffect(() => {
    if (!authUser) {
      setCalendarConnected(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const requestId = calendarRequestIdRef.current + 1;
    calendarRequestIdRef.current = requestId;

    const checkCalendar = async () => {
      try {
        const token = await authUser.getIdToken();
        const response = await fetch(`${apiBaseUrl}/integrations/google/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (isMountedRef.current && requestId === calendarRequestIdRef.current && response.ok) {
          const data = await response.json();
          setCalendarConnected(Boolean(data.connected));
        } else if (isMountedRef.current && requestId === calendarRequestIdRef.current) {
          setCalendarConnected(false);
        }
      } catch {
        if (isMountedRef.current && requestId === calendarRequestIdRef.current) {
          setCalendarConnected(false);
        }
      } finally {
        if (isMountedRef.current && requestId === calendarRequestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    checkCalendar();
  }, [authUser, tick]);

  return {
    calendarConnected,
    emailConfigured,
    isComplete: calendarConnected && emailConfigured,
    isLoading,
    recheck,
  };
}
